/**
 * Per-agent Agent Detail. The dataset carries one base `AgentDetailContent`
 * bundle; rather than author a parallel bundle for every agent, we derive a
 * deterministic variant per agent so each agent's Tasks / Probes / Memory /
 * Autonomy / Evals / Usage tabs differ.
 *
 * The variation is seeded by the agent's pk (stable across reloads) and does two
 * things: (1) ties the headline figures to the agent's own record — Usage's
 * Tasks/Active-users/Avg-eval and the Evals score come straight from the agent,
 * so they match what the library shows; (2) jitters the remaining numbers and
 * varies list *lengths* (not just order), so the lists and figures visibly
 * differ agent to agent. An interactive (non-autonomous) agent additionally has
 * no probes and a quieter autonomy tab.
 */
import type { Agent } from '@wand/shared';
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';

export interface AgentScope {
  pk: string;
  agent?: Agent;
}

function seedFromPk(pk: string): number {
  let h = 0;
  for (let i = 0; i < pk.length; i += 1) h = (h * 31 + pk.charCodeAt(i)) >>> 0;
  return h;
}

/** Rotate an array by n (stable, non-mutating). */
function rot<T>(arr: T[], n: number): T[] {
  if (arr.length < 2) return arr;
  const k = n % arr.length;
  return [...arr.slice(k), ...arr.slice(0, k)];
}

/** Rotate, then take a per-agent count so both order and length differ. */
function pick<T>(arr: T[], seed: number, min = 2, max = 6): T[] {
  const rotated = rot(arr, seed);
  if (arr.length <= min) return rotated;
  const cap = Math.min(max, arr.length);
  const n = min + (seed % (cap - min + 1));
  return rotated.slice(0, n);
}

/** Scale the number inside a display string, preserving prefix/suffix ($, %, k…). */
function scaleNumStr(s: string, f: number): string {
  const m = s.match(/^(\D*)([\d,.]+)(\D*)$/);
  if (!m) return s;
  const [, pre, num, post] = m;
  const n = parseFloat(num!.replace(/,/g, ''));
  if (!Number.isFinite(n)) return s;
  let scaled = n * f;
  if (post === '%') scaled = Math.min(99, scaled);
  const out = num!.includes('.')
    ? (Math.round(scaled * 10) / 10).toString()
    : Math.round(scaled).toLocaleString('en-US');
  return `${pre}${out}${post}`;
}

/** True if the agent has a usable numeric eval like "4.3". */
const hasEval = (a?: Agent): a is Agent => !!a && !!a.eval && a.eval !== '—' && /[\d.]/.test(a.eval);

/**
 * Derive per-agent detail from the base bundle. Returns the base unchanged when
 * there's no agent scope (e.g. off the detail screen).
 */
export function deriveAgentDetail(
  base: AgentDetailContent,
  scope: AgentScope | null,
): AgentDetailContent {
  if (!scope?.pk) return base;
  const s = seedFromPk(scope.pk);
  const agent = scope.agent;
  const interactive = agent ? agent.autonomyMode === 'interactive' || !agent.autonomous : false;
  // Per-agent multiplier in [0.7, 1.3) for jittering figures.
  const f = 0.7 + (s % 61) / 100;

  return {
    ...base,
    memory: {
      shared: pick(base.memory.shared, s, 2, 6),
      private: pick(base.memory.private, s + 1, 1, 4),
      scoped: pick(base.memory.scoped, s + 2, 1, 4),
    },
    skills: { ...base.skills, skills: pick(base.skills.skills, s, 2, 6) },
    tasks: {
      tasks: pick(base.tasks.tasks, s, interactive ? 1 : 3, interactive ? 3 : 8),
      schedules: interactive ? [] : pick(base.tasks.schedules, s + 1, 1, base.tasks.schedules.length),
    },
    autonomy: {
      ...base.autonomy,
      strip: base.autonomy.strip.map((x) => ({ ...x, v: scaleNumStr(x.v, interactive ? f * 0.4 : f) })),
      budget: {
        ...base.autonomy.budget,
        used: scaleNumStr(base.autonomy.budget.used, f),
        pct: Math.min(99, Math.round(base.autonomy.budget.pct * f)),
      },
      limits: base.autonomy.limits.map((l) => ({
        ...l,
        cur: scaleNumStr(l.cur, f),
        pct: Math.min(100, Math.round(l.pct * f)),
      })),
      // Interactive agents don't run an autonomous loop — show fewer decisions.
      decisions: interactive
        ? base.autonomy.decisions.slice(0, 1)
        : pick(base.autonomy.decisions, s, 2, base.autonomy.decisions.length),
      metrics: pick(base.autonomy.metrics, s, 2, base.autonomy.metrics.length),
    },
    // Probes are an autonomous agent's "senses"; an interactive agent has none.
    probes: interactive ? [] : pick(base.probes, s, 1, base.probes.length),
    evals: {
      ...base.evals,
      score: hasEval(agent) ? agent.eval : scaleNumStr(base.evals.score, f),
      dims: base.evals.dims.map((d) => ({
        ...d,
        score: scaleNumStr(d.score, f),
        pct: Math.min(100, Math.round(d.pct * f)),
      })),
      runs: pick(base.evals.runs, s, 2, base.evals.runs.length),
      feedback: pick(base.evals.feedback, s + 1, 1, base.evals.feedback.length),
    },
    usage: {
      ...base.usage,
      kpis: base.usage.kpis.map((k) => {
        if (agent && k.label === 'Tasks') return { ...k, value: agent.tasks };
        if (agent && k.label === 'Active users') return { ...k, value: String(agent.users) };
        if (hasEval(agent) && k.label === 'Avg eval') return { ...k, value: agent.eval };
        return { ...k, value: scaleNumStr(k.value, f) };
      }),
    },
    goals: { ...base.goals, goals: pick(base.goals.goals, s, 1, base.goals.goals.length) },
  };
}
